import React, { useState } from 'react';
import { View, Text, TextInput, Pressable, ActivityIndicator, StyleSheet, Platform } from 'react-native';
import { strings } from '@/constants/residents/strings';
import { UseSignUpFormReturn } from './useSignUpForm';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useLegalContent } from '@/hooks/common/useLegalContent';
import { LegalModal } from '@/components/common/modals';
import { useThemedStyles, useThemeColors } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';

type Props = {
  state: UseSignUpFormReturn;
  onLogin?: () => void;
  onBack?: () => void;
  onPrivacyPolicy?: () => void;
  onTermsOfService?: () => void;
};

export const SignUpForm: React.FC<Props> = ({ 
  state, 
  onLogin,
  onBack,
  onPrivacyPolicy,
  onTermsOfService 
}) => {
  const styles = useThemedStyles(makeStyles);
  const { colors } = useThemeColors();
  const {
    name,
    setName,
    email,
    setEmail,
    userType,
    setUserType,
    isLoading,
    canSubmit,
    submit,
    formError,
    isMobile,
    isWeb,
  } = state;

  const { isMobile: isResponsiveMobile, isTablet, isDesktop } = useResponsive();
  const { showLegalContent, modalVisible, modalContentType, closeModal } = useLegalContent();
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Dynamic styles based on responsive breakpoints
  const dynamicStyles = {
    container: [
      styles.container,
      isResponsiveMobile && styles.containerMobile,
      isTablet && styles.containerTablet,
      isDesktop && styles.containerDesktop
    ],
    formContent: [
      styles.formContent,
      isResponsiveMobile && styles.formContentMobile,
      isDesktop && styles.formContentDesktop
    ],
    inputGroup: [
      styles.inputGroup,
      isResponsiveMobile && styles.inputGroupMobile,
      isDesktop && styles.inputGroupDesktop
    ],
    input: [
      styles.input,
      isResponsiveMobile && styles.inputMobile,
      isDesktop && styles.inputDesktop
    ],
    submitButton: [
      styles.submitButton,
      !canSubmit && styles.submitButtonDisabled,
      isResponsiveMobile && styles.submitButtonMobile,
      isDesktop && styles.submitButtonDesktop
    ]
  };

  const handleSubmit = () => {
    if (isMobile) {
      // Mobile: No terms checkbox required (handled in legal footer)
      if (canSubmit) {
        submit();
      }
    } else {
      // Web: Require terms acceptance
      if (canSubmit && acceptedTerms) {
        submit();
      }
    }
  };

  return (
    <View style={dynamicStyles.container}>
      <View style={dynamicStyles.formContent}>
        {/* User Type Selection - Only show on web (matching resio-web) */}
        {isWeb && (
          <View style={dynamicStyles.inputGroup}>          
          {/* Resident Option */}
          <Pressable
            style={[
              styles.userTypeButton,
              userType === 'resident' && styles.userTypeButtonSelected
            ]}
            onPress={() => setUserType('resident')}
          >
            <View style={styles.userTypeContent}>
              <Text style={[
                styles.userTypeText,
                userType === 'resident' && styles.userTypeTextSelected
              ]}>
                I'm a student or resident - I'm setting up this account for myself
              </Text>
              {userType === 'resident' && (
                <Text style={styles.checkIcon}>✓</Text>
              )}
            </View>
          </Pressable>

          {/* Guarantor/Parent Option */}
          <Pressable
            style={[
              styles.userTypeButton,
              userType === 'guarantor' && styles.userTypeButtonSelected
            ]}
            onPress={() => setUserType('guarantor')}
          >
            <View style={styles.userTypeContent}>
              <Text style={[
                styles.userTypeText,
                userType === 'guarantor' && styles.userTypeTextSelected
              ]}>
                I'm a guarantor, payor, or parent - I'm setting up an account to make payments for a resident
              </Text>
              {userType === 'guarantor' && (
                <Text style={styles.checkIcon}>✓</Text>
              )}
            </View>
          </Pressable>

          {/* Helper text based on selection */}
          {userType && (
            <Text style={styles.helperText}>
              {userType === 'resident' 
                ? "Please enter the email address used when you signed your lease. We'll send a verification message to that address."
                : "Please enter an email address. We'll send a verification message."
              }
            </Text>
          )}
          </View>
        )}

        {/* Name Input - Show always on mobile, after user type selection on web */}
        {(isMobile || userType) && (
          <View style={dynamicStyles.inputGroup}>
            <Text style={styles.label}>Full Name</Text>
            <TextInput
              style={dynamicStyles.input}
              value={name}
              onChangeText={setName}
              placeholder="Enter your full name"
              placeholderTextColor={colors.text.muted}
              autoCapitalize="words"
              autoCorrect={false}
              autoComplete="name"
              returnKeyType="next"
            />
          </View>
        )}

        {/* Email Input - Show always on mobile, after user type selection on web */}
        {(isMobile || userType) && (
          <View style={dynamicStyles.inputGroup}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={dynamicStyles.input}
              value={email}
              onChangeText={setEmail}
              placeholder="Enter your email"
              placeholderTextColor={colors.text.muted}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="email"
              returnKeyType="done"
              onSubmitEditing={handleSubmit}
            />
          </View>
        )}

        {/* Terms and Conditions Checkbox - Only show on web */}
        {isWeb && (
          <View style={styles.checkboxContainer}>
            <Pressable 
              style={[styles.checkbox, acceptedTerms && styles.checkboxChecked]}
              onPress={() => setAcceptedTerms(!acceptedTerms)}
            >
              {acceptedTerms && (
                <Text style={styles.checkmark}>✓</Text>
              )}
            </Pressable>
            <View style={styles.checkboxLabelContainer}>
              <Text style={styles.checkboxLabel}>
                I accept the{' '}
                <Pressable onPress={() => showLegalContent('terms')}>
                  <Text style={styles.link}>Terms and Conditions</Text>
                </Pressable>
              </Text>
            </View>
          </View>
        )}

        {/* Error Message */}
        {!!formError && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{formError}</Text>
          </View>
        )}

        {/* Submit Button */}
        <Pressable
          style={dynamicStyles.submitButton}
          onPress={handleSubmit}
          disabled={isMobile ? !canSubmit : (!canSubmit || !acceptedTerms)}
        >
          {isLoading ? (
            <ActivityIndicator color="#FFFFFF" size="small" />
          ) : (
            <Text style={styles.submitButtonText}>Get started</Text>
          )}
        </Pressable>

        {/* Legal Modal - Only renders on web */}
        <LegalModal
          visible={modalVisible}
          contentType={modalContentType}
          onClose={closeModal}
        />
      </View>
    </View>
  );
};

const makeStyles = ({ colors, semantic }: ThemeContextValue) => StyleSheet.create({
  container: {
    width: '100%',
  },
  // Mobile: Reduced spacing
  containerMobile: {
    paddingHorizontal: 0,
  },
  // Tablet: Medium spacing
  containerTablet: {
    paddingHorizontal: 0,
  },
  // Desktop: Original spacing
  containerDesktop: {
    paddingHorizontal: 0,
  },
  formContent: {
    width: '100%',
  },
  // Mobile: Reduced spacing
  formContentMobile: {
    gap: 16,
  },
  // Desktop: Original spacing (matching web apps)
  formContentDesktop: {
    gap: 20, // space-y-4 md:space-y-10 equivalent
  },
  inputGroup: {
    marginBottom: 24,
  },
  // Mobile: Reduced margin
  inputGroupMobile: {
    marginBottom: 16,
  },
  // Desktop: Original margin (matching web apps)
  inputGroupDesktop: {
    marginBottom: 5,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text.primary,
    marginBottom: 8,
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
  input: {
    height: 48,
    borderWidth: 1,
    borderColor: semantic.border.secondary,
    borderRadius: 6,
    paddingHorizontal: 16,
    fontSize: 16,
    color: colors.text.primary,
    backgroundColor: semantic.background.input,
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
    ...Platform.select({
      web: {
        outlineStyle: 'none' as any,
        '&:focus': {
          borderColor: semantic.interactive.primary,
          boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)' as any,
        },
      },
    }),
  },
  // Mobile: Slightly smaller input
  inputMobile: {
    height: 44,
    fontSize: 16,
  },
  // Desktop: Original size (matching web apps)
  inputDesktop: {
    height: 48,
    fontSize: 16,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 24,
    paddingHorizontal: 0,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 1,
    borderColor: semantic.border.secondary,
    borderRadius: 4,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    marginTop: 2, // Align with text baseline
  },
  checkboxChecked: {
    backgroundColor: '#f55560', // Pink-500 (matching dark:checked:bg-pink-500)
    borderColor: '#f55560',
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 'bold',
  },
  checkboxLabelContainer: {
    flex: 1,
  },
  checkboxLabel: {
    fontSize: 14,
    color: colors.text.primary,
    lineHeight: 20,
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
  link: {
    color: '#f55560', // Pink-500 (matching dark:text-pink-500)
    textDecorationLine: 'underline',
    fontSize: 14,
    fontWeight: '500',
  },
  errorContainer: {
    marginBottom: 16,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: semantic.background.tertiary,
    borderColor: semantic.status.error,
    borderWidth: 1,
    borderRadius: 6,
  },
  errorText: {
    color: semantic.status.error,
    fontSize: 14,
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
  submitButton: {
    backgroundColor: semantic.interactive.primary,
    borderRadius: 6,
    paddingVertical: 12,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        transition: 'all 0.2s ease-in-out' as any,
        '&:hover': {
          backgroundColor: semantic.interactive.primaryHover as any,
        },
        '&:focus': {
          outline: 'none' as any,
          boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.3)' as any,
        },
      },
    }),
  },
  submitButtonDisabled: {
    backgroundColor: '#6B7280', // gray-500
    opacity: 0.6,
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as any,
        '&:hover': {
          backgroundColor: '#6B7280' as any,
        },
      },
    }),
  },
  // Mobile: Full width button
  submitButtonMobile: {
    width: '100%',
  },
  // Desktop: Full width button (matching web apps)
  submitButtonDesktop: {
    width: '100%',
    backgroundColor: semantic.interactive.secondary,
  },
  submitButtonText: {
    color: '#FFFFFF', // on-fill: interactive.secondary is resioPink in both themes
    fontSize: 16,
    fontWeight: '600',
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
  // User Type Selection Styles (matching resio-web)
  sectionLabel: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text.primary,
    marginBottom: 16,
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
  userTypeButton: {
    borderWidth: 1,
    borderColor: semantic.border.secondary,
    borderRadius: 6,
    padding: 16,
    marginBottom: 16,
    backgroundColor: 'transparent',
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        transition: 'all 0.2s ease-in-out' as any,
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.05)' as any,
        },
      },
    }),
  },
  userTypeButtonSelected: {
    borderColor: semantic.interactive.primary,
    // backgroundColor: 'rgba(59, 130, 246, 0.1)',
    // ...Platform.select({
    //   web: {
    //     boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)' as any,
    //   },
    // }),
  },
  userTypeContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  userTypeText: {
    fontSize: 16,
    color: colors.text.primary,
    flex: 1,
    lineHeight: 22,
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
  userTypeTextSelected: {
    // color: '#3B82F6', // blue-500
    fontWeight: '500',
  },
  checkIcon: {
    fontSize: 16,
    color: semantic.interactive.primary,
    fontWeight: 'bold',
    marginLeft: 12,
  },
  helperText: {
    fontSize: 14,
    color: semantic.text.link,
    lineHeight: 20,
    marginTop: 8,
    fontFamily: Platform.select({
      web: 'system-ui, -apple-system, sans-serif',
      default: 'System',
    }),
  },
});
